import {
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
  type TableHTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes
} from 'react';

import type { SortDirection } from '../../../types/globals';

import './Table.scss';

export type TableDensity = 'sm' | 'md';
export type TableRowVariant = 'default' | 'accepted';

// ─── Root ────────────────────────────────────────────────────────────────────

export interface TableProps extends Omit<TableHTMLAttributes<HTMLTableElement>, 'children'> {
  /** Row height density. `md` (default) for list-style tables; `sm` for the
   *  tighter rate / census tables (6–7px cell padding). */
  density?: TableDensity;
  children?: ReactNode;
  className?: string;
}

const TableRoot = forwardRef<HTMLTableElement, TableProps>(function TableRoot(
  { density = 'md', className, children, ...rest },
  ref
) {
  const composed = ['table', `table--density-${density}`, className].filter(Boolean).join(' ');
  return (
    <table ref={ref} className={composed} {...rest}>
      {children}
    </table>
  );
});

// ─── Head ────────────────────────────────────────────────────────────────────

export interface TableHeadProps extends HTMLAttributes<HTMLTableSectionElement> {
  children?: ReactNode;
}

const TableHead = ({ className, children, ...rest }: TableHeadProps) => {
  const composed = ['table__head', className].filter(Boolean).join(' ');
  return (
    <thead className={composed} {...rest}>
      {children}
    </thead>
  );
};

// ─── Body ────────────────────────────────────────────────────────────────────

export interface TableBodyProps extends HTMLAttributes<HTMLTableSectionElement> {
  children?: ReactNode;
}

const TableBody = ({ className, children, ...rest }: TableBodyProps) => {
  const composed = ['table__body', className].filter(Boolean).join(' ');
  return (
    <tbody className={composed} {...rest}>
      {children}
    </tbody>
  );
};

// ─── Row ─────────────────────────────────────────────────────────────────────

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  /** `accepted` tints the row with `--success-bg`. */
  variant?: TableRowVariant;
  /** Adds the pointer cursor and hover tint for rows that navigate on click. */
  interactive?: boolean;
  children?: ReactNode;
}

const TableRow = ({ variant = 'default', interactive = false, className, children, ...rest }: TableRowProps) => {
  const composed = [
    'table__row',
    variant === 'default' ? '' : `table__row--${variant}`,
    interactive ? 'table__row--interactive' : '',
    className
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <tr className={composed} {...rest}>
      {children}
    </tr>
  );
};

// ─── Header cell ─────────────────────────────────────────────────────────────

export interface TableHeaderCellProps extends ThHTMLAttributes<HTMLTableCellElement> {
  children?: ReactNode;
}

const TableHeaderCell = ({ className, children, ...rest }: TableHeaderCellProps) => {
  const composed = ['table__head-cell', className].filter(Boolean).join(' ');
  return (
    <th className={composed} {...rest}>
      {children}
    </th>
  );
};

// ─── Sortable header cell ────────────────────────────────────────────────────

export interface TableSortableHeaderCellProps extends Omit<ThHTMLAttributes<HTMLTableCellElement>, 'aria-sort'> {
  /** `true` when this column drives the current sort. */
  active?: boolean;
  direction?: SortDirection;
  onToggle: () => void;
  /** Accessible name for the toggle. */
  toggleLabel?: string;
  children?: ReactNode;
}

const TableSortableHeaderCell = ({
  active = false,
  direction = 'asc',
  onToggle,
  toggleLabel,
  className,
  children,
  ...rest
}: TableSortableHeaderCellProps) => {
  const currentOrder = direction === 'asc' ? 'ascending' : 'descending';
  const ariaSort = active ? currentOrder : 'none';

  return (
    <TableHeaderCell className={className} aria-sort={ariaSort} {...rest}>
      <button type="button" className="table__sort" onClick={onToggle} aria-label={toggleLabel}>
        {children}
        <span className="table__sort-arrow" aria-hidden="true">
          {active && (direction === 'asc' ? '↑' : '↓')}
        </span>
      </button>
    </TableHeaderCell>
  );
};

// ─── Cell ────────────────────────────────────────────────────────────────────

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  children?: ReactNode;
}

const TableCell = ({ className, children, ...rest }: TableCellProps) => {
  const composed = ['table__cell', className].filter(Boolean).join(' ');
  return (
    <td className={composed} {...rest}>
      {children}
    </td>
  );
};

// ─── Compound export ─────────────────────────────────────────────────────────

type TableComponent = typeof TableRoot & {
  Head: typeof TableHead;
  Body: typeof TableBody;
  Row: typeof TableRow;
  HeaderCell: typeof TableHeaderCell;
  SortableHeaderCell: typeof TableSortableHeaderCell;
  Cell: typeof TableCell;
};

const Table = TableRoot as TableComponent;
Table.Head = TableHead;
Table.Body = TableBody;
Table.Row = TableRow;
Table.HeaderCell = TableHeaderCell;
Table.SortableHeaderCell = TableSortableHeaderCell;
Table.Cell = TableCell;

Table.displayName = 'Table';
TableHead.displayName = 'Table.Head';
TableBody.displayName = 'Table.Body';
TableRow.displayName = 'Table.Row';
TableHeaderCell.displayName = 'Table.HeaderCell';
TableSortableHeaderCell.displayName = 'Table.SortableHeaderCell';
TableCell.displayName = 'Table.Cell';

export default Table;
